import { Keyboard, Loader2, Mic, MicOff, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

interface VoiceInterviewStubProps {
	isRecording: boolean;
	transcript: string;
	interimTranscript: string;
	isSpeechSupported: boolean;
	isSubmitting: boolean;
	questionIndex: number;
	existingAnswer?: string;
	sttError?: string | null;
	forceTextMode?: boolean;
	isActive?: boolean;
	onStartRecording: () => void;
	onStopRecording: () => void;
	onSubmit: (text: string, source: "voice" | "text") => void;
}

/**
 * Unified answer recorder with a single textarea that's always visible.
 *
 * - Voice mode: textarea is read-only, mic button visible, transcript populates as you speak
 * - Text mode: textarea is editable, mic button hidden
 * - No layout jumps between modes
 */
export function VoiceInterviewStub({
	isRecording,
	transcript,
	interimTranscript,
	isSpeechSupported,
	isSubmitting,
	questionIndex,
	existingAnswer,
	sttError,
	forceTextMode = false,
	isActive = false,
	onStartRecording,
	onStopRecording,
	onSubmit,
}: VoiceInterviewStubProps) {
	const preferText =
		forceTextMode ||
		(typeof window !== "undefined" &&
			localStorage.getItem("helm_onboarding_mode") === "text");
	const [textInput, setTextInput] = useState(existingAnswer ?? "");
	const [textMode, setTextMode] = useState(preferText);
	const [usedStt, setUsedStt] = useState(false);

	// Reset text input when navigating between questions
	useEffect(() => {
		setTextInput(existingAnswer ?? "");
		setUsedStt(false);
	}, [questionIndex, existingAnswer]);

	// Auto-start recording when answer section becomes active or question changes in voice mode.
	// Respect the persisted mode: don't auto-start if user explicitly chose text (AME-340).
	const prevActiveRef = useRef(false);
	const prevQuestionIndexRef = useRef(questionIndex);
	useEffect(() => {
		const persistedMode = typeof window !== "undefined"
			? localStorage.getItem("helm_onboarding_mode")
			: null;
		const userChoseText = persistedMode === "text";
		const justBecameActive = isActive && !prevActiveRef.current;
		const questionChanged = isActive && questionIndex !== prevQuestionIndexRef.current;
		if ((justBecameActive || questionChanged) && !textMode && !userChoseText && isSpeechSupported && !isRecording) {
			setUsedStt(true);
			onStartRecording();
		}
		prevActiveRef.current = isActive;
		prevQuestionIndexRef.current = questionIndex;
	}, [isActive, questionIndex, textMode, isSpeechSupported, isRecording, onStartRecording]);
	const prevRecordingRef = useRef(false);
	const questionIndexRef = useRef(questionIndex);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const hasShownMicError = useRef(false);
	const lastSttErrorRef = useRef<string | null>(null);
	const sttErrorInTextMode = useRef(false);

	// Keep questionIndexRef in sync
	useEffect(() => {
		questionIndexRef.current = questionIndex;
	}, [questionIndex]);

	// Auto-switch to text mode if speech not supported
	useEffect(() => {
		if (!isSpeechSupported && !hasShownMicError.current) {
			hasShownMicError.current = true;
			setTextMode(true);
			toast.error("Microphone unavailable", {
				description: "Switched to text input. Check browser permissions.",
			});
		}
	}, [isSpeechSupported]);

	// Auto-switch to text mode on async STT errors
	useEffect(() => {
		if (sttError) {
			if (!sttErrorInTextMode.current) {
				sttErrorInTextMode.current = true;
				setTextMode(true);
				toast.error("Recording failed", {
					description: `${sttError}. Switched to text input.`,
				});
			}
			lastSttErrorRef.current = sttError;
		} else {
			sttErrorInTextMode.current = false;
		}
	}, [sttError]);

	// When recording stops, append final transcript to textInput.
	// Guard with questionIndexRef: if the question changed (e.g. submit advanced
	// to the next question), skip appending stale transcript from the old question.
	const appendQuestionRef = useRef(questionIndex);
	useEffect(() => {
		if (prevRecordingRef.current && !isRecording && transcript) {
			if (appendQuestionRef.current === questionIndexRef.current) {
				setTextInput((prev) => {
					const separator = prev.trim() ? " " : "";
					return `${prev.trim()}${separator}${transcript.trim()}`;
				});
			}
		}
		if (!prevRecordingRef.current && isRecording) {
			// Capture which question we started recording on
			appendQuestionRef.current = questionIndexRef.current;
		}
		prevRecordingRef.current = isRecording;
	}, [isRecording, transcript]);

	// In voice mode, show live transcript in the textarea
	const displayValue = isRecording
		? [textInput.trim(), transcript, interimTranscript]
				.filter(Boolean)
				.join(" ")
		: textInput;

	const hasContent =
		textInput.trim().length > 0 || transcript.trim().length > 0;

	const handleMicPress = () => {
		if (isRecording) {
			onStopRecording();
		} else {
			setUsedStt(true);
			onStartRecording();
		}
	};

	const handleSubmit = () => {
		if (!hasContent) return;
		// Include interimTranscript to avoid truncation (AME-337)
		const finalText = isRecording
			? [textInput.trim(), transcript.trim(), interimTranscript.trim()].filter(Boolean).join(" ")
			: textInput.trim();
		if (!finalText) return;
		// Stop recording before submitting to prevent bleed (AME-334/338)
		if (isRecording) {
			onStopRecording();
		}
		// Clear text immediately so it doesn't bleed into the next question
		setTextInput("");
		onSubmit(finalText, usedStt ? "voice" : "text");
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
			e.preventDefault();
			handleSubmit();
		}
	};

	const switchToTextMode = () => {
		if (isRecording) onStopRecording();
		setTextMode(true);
		// Persist mode choice so nav back respects it (AME-340)
		localStorage.setItem("helm_onboarding_mode", "text");
		requestAnimationFrame(() => textareaRef.current?.focus());
	};

	const switchToVoiceMode = () => {
		if (isSpeechSupported) {
			setTextMode(false);
			setUsedStt(true);
			// Persist mode choice (AME-340)
			localStorage.setItem("helm_onboarding_mode", "voice");
			onStartRecording();
		} else {
			toast.error("Microphone unavailable", {
				description: "Check your browser microphone permissions.",
			});
		}
	};

	const isVoiceMode = !textMode;

	return (
		<div className="space-y-4">
			{/* Voice controls — mic button + visualization */}
			{isVoiceMode && (
				<div className="flex flex-col items-center gap-3">
					{isRecording && <AudioVisualizationBars />}
					<button
						type="button"
						onClick={handleMicPress}
						disabled={isSubmitting}
						className={`
							relative h-16 w-16 rounded-full flex items-center justify-center
							transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60
							${
								isRecording
									? "bg-destructive text-destructive-foreground shadow-lg scale-110"
									: "bg-primary text-primary-foreground shadow-md hover:shadow-lg hover:scale-105 active:scale-95"
							}
							${isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
						`}
						aria-label={isRecording ? "Stop recording" : "Tap to record"}
					>
						{isRecording && (
							<span className="absolute inset-0 rounded-full animate-ping bg-destructive/30" />
						)}
						{isRecording ? (
							<MicOff className="h-6 w-6 relative z-10" />
						) : (
							<Mic className="h-6 w-6 relative z-10" />
						)}
					</button>
					<p className="text-xs font-medium text-muted-foreground">
						{isRecording ? "Tap to stop" : "Tap to record"}
					</p>
				</div>
			)}

			{/* Unified textarea — always visible, auto-expanding (AME-333) */}
			<div
				className="relative transition-opacity duration-300"
				style={{ opacity: isSubmitting ? 0.5 : 1 }}
			>
				<textarea
					ref={textareaRef}
					value={displayValue}
					onChange={(e) => {
						// Auto-switch to text mode if user starts typing in voice mode
						if (!textMode) {
							if (isRecording) onStopRecording();
							setTextMode(true);
							localStorage.setItem("helm_onboarding_mode", "text");
						}
						setTextInput(e.target.value);
						// Auto-expand textarea to fit content
						const ta = e.target;
						ta.style.height = "auto";
						ta.style.height = `${Math.min(ta.scrollHeight, 320)}px`;
					}}
					onKeyDown={handleKeyDown}
					placeholder={
						isVoiceMode
							? "Your answer will appear here as you speak..."
							: "Type your answer..."
					}
					className={`
						w-full min-h-[80px] max-h-[320px] overflow-y-auto rounded-lg border px-4 py-3 text-foreground
						placeholder:text-muted-foreground resize-none transition-colors duration-200
						${
							isVoiceMode
								? "border-border/60 bg-muted/30 cursor-default focus:outline-none"
								: "border-border bg-card focus:outline-none focus:ring-2 focus:ring-primary/40"
						}
					`}
					disabled={isSubmitting}
					autoFocus={textMode}
				/>
				{/* Recording indicator overlay */}
				{isVoiceMode && isRecording && displayValue && (
					<span className="absolute bottom-3 right-3 inline-block w-0.5 h-4 bg-primary animate-pulse" />
				)}
			</div>

			{/* Actions row */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					{isVoiceMode ? (
						<button
							type="button"
							onClick={switchToTextMode}
							className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
						>
							<Keyboard className="size-3.5" />
							Type instead
						</button>
					) : isSpeechSupported ? (
						<button
							type="button"
							onClick={switchToVoiceMode}
							className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
						>
							<Mic className="size-3.5" />
							Switch to voice
						</button>
					) : null}
					{textMode && (
						<span className="text-xs text-muted-foreground">
							{navigator.platform.includes("Mac") ? "\u2318" : "Ctrl"}+Enter to
							submit
						</span>
					)}
				</div>
				<Button
					onClick={handleSubmit}
					disabled={!hasContent || isSubmitting}
					size="sm"
				>
					{isSubmitting ? (
						<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
					) : (
						<Send className="h-3.5 w-3.5 mr-1.5" />
					)}
					{isSubmitting ? "Saving..." : "Submit Answer"}
				</Button>
			</div>
		</div>
	);
}

/**
 * Animated audio visualization bars displayed during recording.
 */
function AudioVisualizationBars() {
	const barCount = 12;
	return (
		<div className="flex items-center justify-center gap-[3px] h-8">
			{Array.from({ length: barCount }, (_, i) => (
				<div
					key={i}
					className="w-1 rounded-full bg-primary/70"
					style={{
						animation: `voice-bar 0.8s ease-in-out ${i * 0.07}s infinite alternate`,
					}}
				/>
			))}
			<style>{`
				@keyframes voice-bar {
					0% { height: 6px; opacity: 0.4; }
					100% { height: 28px; opacity: 1; }
				}
			`}</style>
		</div>
	);
}
